import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { LogoMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { logoutBoardSession, getSecretaryData } from "@workspace/api-client-react";
import type { SecretaryData } from "@workspace/api-client-react";
import {
  ArrowLeft, Loader2, Mail, MailOpen, Users, ScrollText, FileText,
  RefreshCw, LogOut, Inbox, ClipboardList,
} from "lucide-react";

function fmt(s: string) {
  return new Date(s).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function fmtDate(s: string) {
  return new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const ROLE_COLORS: Record<string, string> = {
  donor: "bg-blue-100 text-blue-800 border-blue-200",
  debtor: "bg-orange-100 text-orange-800 border-orange-200",
  admin: "bg-purple-100 text-purple-800 border-purple-200",
};

export default function SecretaryDashboard() {
  const [, navigate] = useLocation();
  const [inboxPage, setInboxPage] = useState(1);
  const [selectedMsg, setSelectedMsg] = useState<SecretaryData["inbox"]["messages"][number] | null>(null);

  const { data, isLoading, isFetching, dataUpdatedAt, refetch } = useQuery<SecretaryData>({
    queryKey: ["secretary-data", inboxPage],
    queryFn: () => getSecretaryData(inboxPage),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  async function handleLogout() {
    await logoutBoardSession();
    navigate("/board-entry");
  }

  return (
    <div className="min-h-screen bg-background">
      <nav className="sticky top-0 z-50 border-b bg-slate-900 border-slate-700">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => navigate("/board-entry")}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Exit
            </Button>
            <LogoMark size={26} />
            <div>
              <span className="text-sm font-semibold text-white">Secretary Portal</span>
              <span className="ml-2 text-xs text-slate-400 hidden sm:inline">DebtBridge 501(c)(3)</span>
            </div>
            <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-600 text-white tracking-wide">ADMIN</span>
          </div>
          <div className="flex items-center gap-2">
            {isFetching && <RefreshCw className="h-3.5 w-3.5 animate-spin text-slate-400" />}
            {dataUpdatedAt > 0 && (
              <span className="text-xs text-slate-400 hidden sm:inline">
                Updated {fmt(new Date(dataUpdatedAt).toISOString())}
              </span>
            )}
            <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => void refetch()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => void handleLogout()}>
              <LogOut className="h-4 w-4 mr-1" />
              Sign out
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Secretary Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Communications, member registry, audit log, and application overview.
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : !data ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center text-red-800 text-sm">
            Failed to load data. Please refresh.
          </div>
        ) : (
          <>
            {/* Stat cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Inbox Messages", value: data.inbox.total, icon: Inbox, accent: "bg-blue-500" },
                { label: "Total Members", value: data.members.length, icon: Users, accent: "bg-emerald-500" },
                { label: "Applications", value: data.debtStats.total, icon: FileText, accent: "bg-amber-500" },
                { label: "Audit Events", value: data.auditLogs.length, icon: ScrollText, accent: "bg-purple-500" },
              ].map(({ label, value, icon: Icon, accent }) => (
                <div key={label} className="rounded-xl border bg-card p-5 flex items-start gap-4">
                  <div className={`rounded-lg p-2.5 ${accent}`}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-medium">{label}</p>
                    <p className="text-2xl font-bold tracking-tight mt-0.5">{value}</p>
                  </div>
                </div>
              ))}
            </div>

            <Tabs defaultValue="inbox">
              <TabsList>
                <TabsTrigger value="inbox" className="flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5" />
                  Inbox
                  {data.inbox.total > 0 && (
                    <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-[10px]">
                      {data.inbox.total}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="members">
                  <Users className="h-3.5 w-3.5 mr-1.5" />
                  Members
                </TabsTrigger>
                <TabsTrigger value="applications">
                  <FileText className="h-3.5 w-3.5 mr-1.5" />
                  Applications
                </TabsTrigger>
                <TabsTrigger value="audit">
                  <ClipboardList className="h-3.5 w-3.5 mr-1.5" />
                  Audit Log
                </TabsTrigger>
              </TabsList>

              {/* ── Inbox ── */}
              <TabsContent value="inbox" className="mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-xl border overflow-hidden">
                    <div className="bg-muted/40 px-4 py-2.5 border-b text-xs font-medium text-muted-foreground">
                      Messages ({data.inbox.total})
                    </div>
                    {data.inbox.messages.length === 0 ? (
                      <div className="p-8 text-center text-sm text-muted-foreground">No messages.</div>
                    ) : (
                      <div className="divide-y max-h-[500px] overflow-y-auto">
                        {data.inbox.messages.map((m) => (
                          <button
                            key={m.id}
                            onClick={() => setSelectedMsg(m)}
                            className={`w-full text-left px-4 py-3 hover:bg-muted/30 transition-colors ${selectedMsg?.id === m.id ? "bg-muted/40" : ""}`}
                          >
                            <div className="flex items-center gap-2 mb-0.5">
                              {m.isRead
                                ? <MailOpen className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                : <Mail className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                              }
                              <span className={`text-sm truncate ${!m.isRead ? "font-semibold" : ""}`}>
                                {m.fromEmail}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground truncate ml-5">{m.subject}</p>
                            <p className="text-[10px] text-muted-foreground ml-5 mt-0.5">{fmt(m.receivedAt)}</p>
                          </button>
                        ))}
                      </div>
                    )}
                    {data.inbox.pages > 1 && (
                      <div className="flex items-center justify-between px-4 py-2.5 border-t bg-muted/20 text-xs text-muted-foreground">
                        <button
                          onClick={() => setInboxPage((p) => Math.max(1, p - 1))}
                          disabled={inboxPage === 1}
                          className="disabled:opacity-40 hover:text-foreground transition-colors"
                        >
                          ← Prev
                        </button>
                        <span>Page {inboxPage} of {data.inbox.pages}</span>
                        <button
                          onClick={() => setInboxPage((p) => Math.min(data.inbox.pages, p + 1))}
                          disabled={inboxPage >= data.inbox.pages}
                          className="disabled:opacity-40 hover:text-foreground transition-colors"
                        >
                          Next →
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="rounded-xl border overflow-hidden">
                    <div className="bg-muted/40 px-4 py-2.5 border-b text-xs font-medium text-muted-foreground">
                      Message Preview
                    </div>
                    {!selectedMsg ? (
                      <div className="p-8 text-center text-sm text-muted-foreground">Select a message to read</div>
                    ) : (
                      <div className="p-4 space-y-3">
                        <div>
                          <p className="text-xs text-muted-foreground">From</p>
                          <p className="text-sm font-medium">{selectedMsg.fromEmail}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Subject</p>
                          <p className="text-sm font-medium">{selectedMsg.subject}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Received</p>
                          <p className="text-sm">{fmt(selectedMsg.receivedAt)}</p>
                        </div>
                        <div className="border-t pt-3">
                          <p className="text-xs text-muted-foreground mb-1">Body</p>
                          <div className="bg-muted/30 rounded-lg p-3 text-sm whitespace-pre-wrap max-h-60 overflow-y-auto">
                            {selectedMsg.textBody ?? "(No body)"}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* ── Members ── */}
              <TabsContent value="members" className="mt-4">
                <div className="rounded-xl border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40">
                      <tr>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Member</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground hidden sm:table-cell">Role</th>
                        <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.members.map((u) => (
                        <tr key={u.id} className="border-t">
                          <td className="px-4 py-3">
                            <p className="font-medium text-sm">{u.displayName ?? "—"}</p>
                            <p className="text-xs text-muted-foreground">{u.email}</p>
                          </td>
                          <td className="px-4 py-3 hidden sm:table-cell">
                            <Badge variant="outline" className={`text-xs capitalize ${ROLE_COLORS[u.role] ?? "bg-gray-100 text-gray-700"}`}>
                              {u.role}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right text-xs text-muted-foreground whitespace-nowrap">
                            {fmtDate(u.createdAt)}
                          </td>
                        </tr>
                      ))}
                      {data.members.length === 0 && (
                        <tr>
                          <td colSpan={3} className="px-4 py-6 text-center text-muted-foreground text-sm">No members yet.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              {/* ── Applications ── */}
              <TabsContent value="applications" className="mt-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                  {[
                    { label: "Pending Review", value: data.debtStats.pending, color: "text-amber-600" },
                    { label: "In Queue", value: data.debtStats.queued, color: "text-blue-600" },
                    { label: "Processing", value: data.debtStats.processing, color: "text-indigo-600" },
                    { label: "Cleared", value: data.debtStats.paid, color: "text-green-600" },
                    { label: "Rejected", value: data.debtStats.rejected, color: "text-red-600" },
                    { label: "Total", value: data.debtStats.total, color: "text-foreground" },
                  ].map(({ label, value, color }) => (
                    <div key={label} className="rounded-xl border bg-card p-4">
                      <p className="text-xs text-muted-foreground">{label}</p>
                      <p className={`text-3xl font-bold mt-1 ${color}`}>{value.toLocaleString()}</p>
                    </div>
                  ))}
                </div>
                <div className="rounded-xl border p-4 bg-muted/20 text-sm text-muted-foreground text-center">
                  Application status overview — contact the administrator for full debt application details.
                </div>
              </TabsContent>

              {/* ── Audit Log ── */}
              <TabsContent value="audit" className="mt-4">
                <div className="rounded-xl border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40">
                      <tr>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Action</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground hidden sm:table-cell">Actor</th>
                        <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">When</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.auditLogs.map((l) => (
                        <tr key={l.id} className="border-t">
                          <td className="px-4 py-2.5">
                            <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
                              {l.action}
                            </span>
                            {l.entityType && l.entityId && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                {l.entityType} #{l.entityId}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-xs text-muted-foreground hidden sm:table-cell">
                            {l.actorEmail ?? "—"}
                          </td>
                          <td className="px-4 py-2.5 text-xs text-muted-foreground text-right whitespace-nowrap">
                            {fmt(l.createdAt)}
                          </td>
                        </tr>
                      ))}
                      {data.auditLogs.length === 0 && (
                        <tr>
                          <td colSpan={3} className="px-4 py-6 text-center text-muted-foreground text-sm">No audit events recorded.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </TabsContent>
            </Tabs>

            <div className="text-center pt-4 border-t">
              <p className="text-xs text-muted-foreground">
                DebtBridge 501(c)(3) · Secretary portal · Read-only
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
